import {
  HHAlert,
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { InputSummaryView } from '../components/intake/input-summary-view';
import { JsonTree } from '../components/json-tree/json-tree';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { ContractInput, initialWizardState } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { getOrgGroups } from '../utils/org-groups';
import { contractInputSchema } from '../validation/contract-input-schema';

type ValidationState =
  | { kind: 'empty' }
  | { kind: 'parsing' }
  | { kind: 'invalidJson'; message: string }
  | { kind: 'invalidSchema'; errors: string[] }
  | { kind: 'valid' };

const PARSE_DEBOUNCE_MS = 300;

export const IntakeStep = (): JSX.Element => {
  const { state, setState } = useWizardState();
  const [tab, setTab] = useState<'summary' | 'tree'>('summary');
  const [validation, setValidation] = useState<ValidationState>(() =>
    state.input ? { kind: 'valid' } : { kind: 'empty' },
  );

  useStepHeader({
    description:
      'Paste the structured contract JSON. The wizard parses, validates, and summarizes it before walking you through dedupe-aware creates.',
  });

  useEffect(() => {
    const raw = state.rawInput;
    if (!raw.trim()) {
      setValidation({ kind: 'empty' });
      if (state.input != null) setState(s => ({ ...s, input: null }));
      return undefined;
    }

    setValidation({ kind: 'parsing' });
    const handle = window.setTimeout(async () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        setValidation({
          kind: 'invalidJson',
          message: (e as Error).message,
        });
        if (state.input != null) setState(s => ({ ...s, input: null }));
        return;
      }
      try {
        const valid = await contractInputSchema.validate(parsed, {
          abortEarly: false,
          strict: false,
        });
        setValidation({ kind: 'valid' });
        setState(s => {
          const newInput = valid as unknown as ContractInput;
          if (JSON.stringify(s.input) === JSON.stringify(newInput)) return s;
          // Seed the orgNames decision map. Single-TIN input gets a one-entry
          // map auto-populated from input.entity_legal_name; multi-TIN input
          // seeds only the first TIN and lets the operator type the rest in
          // the names panel below.
          const groups = getOrgGroups(newInput);
          const seededOrgNames: Record<string, string> = {};
          if (groups.length > 0) {
            seededOrgNames[groups[0].tax_id] = newInput.entity_legal_name;
          }
          return {
            ...initialWizardState,
            rawInput: s.rawInput,
            input: newInput,
            decisions: {
              ...initialWizardState.decisions,
              orgNames: seededOrgNames,
            },
          };
        });
      } catch (e) {
        const errs =
          e instanceof yup.ValidationError ? e.errors : [(e as Error).message];
        setValidation({ kind: 'invalidSchema', errors: errs });
        if (state.input != null) setState(s => ({ ...s, input: null }));
      }
    }, PARSE_DEBOUNCE_MS);

    return (): void => window.clearTimeout(handle);
    // state.input intentionally omitted: re-running the parse on input changes
    // we just made would loop. Only the operator's edits to rawInput should
    // trigger validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rawInput, setState]);

  // Distinct TINs in input order. Multi-TIN input forces the operator to
  // type a legal entity name for each TIN before continuing — single-TIN
  // input auto-seeds from input.entity_legal_name and skips the panel.
  const orgGroups = useMemo(
    () => (state.input != null ? getOrgGroups(state.input) : []),
    [state.input],
  );
  const isMultiOrg = orgGroups.length > 1;
  const orgNames = state.decisions.orgNames;
  const allOrgNamesFilled = orgGroups.every(
    g =>
      typeof orgNames[g.tax_id] === 'string' &&
      orgNames[g.tax_id].trim() !== '',
  );

  const canContinue =
    validation.kind === 'valid' && state.input != null && allOrgNamesFilled;
  const disabledInfo: DisabledInfo | undefined = (():
    | DisabledInfo
    | undefined => {
    if (canContinue) return undefined;
    if (validation.kind === 'empty') {
      return { message: 'Paste contract JSON to begin.', severity: 'info' };
    }
    if (validation.kind === 'parsing') {
      return { message: 'Validating JSON…', severity: 'info' };
    }
    if (validation.kind === 'invalidJson') {
      return {
        message: `JSON is invalid: ${validation.message}`,
        severity: 'error',
      };
    }
    if (validation.kind === 'invalidSchema') {
      return {
        message: `Schema validation failed (${validation.errors.length} issue${validation.errors.length === 1 ? '' : 's'}). See errors above.`,
        severity: 'warning',
      };
    }
    if (!allOrgNamesFilled) {
      return {
        message:
          'Multiple TINs detected. Type a legal entity name for each TIN below before continuing.',
        severity: 'warning',
      };
    }
    return undefined;
  })();

  const setOrgName = (taxId: string, name: string): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        orgNames: { ...s.decisions.orgNames, [taxId]: name },
      },
    }));
  };

  const parsedForTree = useMemo<unknown>(() => {
    if (state.input != null) return state.input;
    if (!state.rawInput.trim()) return null;
    try {
      return JSON.parse(state.rawInput);
    } catch {
      return null;
    }
  }, [state.input, state.rawInput]);

  return (
    <HHStack spacing={3}>
      <HHTextField
        hhVariant="variant-bypass"
        multiline
        minRows={12}
        maxRows={24}
        placeholder="Paste contract JSON here..."
        value={state.rawInput}
        onChange={(e): void =>
          setState(s => ({ ...s, rawInput: e.target.value }))
        }
        InputProps={{
          sx: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 12,
          },
        }}
        fullWidth
      />

      <HHStack direction="row" spacing={1.5} alignItems="center">
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          color="primary"
          size="small"
          disabled={state.rawInput.trim() === ''}
          onClick={(): void => {
            try {
              const pretty = JSON.stringify(
                JSON.parse(state.rawInput),
                null,
                2,
              );
              setState(s => ({ ...s, rawInput: pretty }));
            } catch {
              // Leave the textarea alone if it's not valid JSON — the
              // validation alert below already explains what's wrong.
            }
          }}
        >
          Format JSON
        </HHButton>
        <HHButton
          hhVariant="variant-bypass"
          variant="text"
          color="primary"
          size="small"
          disabled={state.rawInput === ''}
          onClick={(): void => setState(s => ({ ...s, rawInput: '' }))}
        >
          Clear
        </HHButton>
        <span style={{ flex: 1 }} />
        <HHTypography
          hhVariant="caption"
          sx={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}
        >
          {state.rawInput.length.toLocaleString()} chars
        </HHTypography>
      </HHStack>

      {validation.kind === 'invalidJson' && (
        <HHAlert hhVariant="outlined" severity="error">
          <HHTypography hhVariant="subtitle2">JSON parse error</HHTypography>
          <HHTypography hhVariant="body2">{validation.message}</HHTypography>
        </HHAlert>
      )}
      {validation.kind === 'invalidSchema' && (
        <HHAlert hhVariant="outlined" severity="error">
          <HHTypography hhVariant="subtitle2">
            {`Schema validation failed (${validation.errors.length} issue${
              validation.errors.length === 1 ? '' : 's'
            })`}
          </HHTypography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {validation.errors.map(err => (
              <li key={err}>
                <HHTypography hhVariant="body2">{err}</HHTypography>
              </li>
            ))}
          </ul>
        </HHAlert>
      )}

      {state.input != null && isMultiOrg && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={1}>
            <HHTypography hhVariant="subtitle2">
              Multiple TINs detected — name each legal entity
            </HHTypography>
            <HHTypography hhVariant="body2" sx={{ opacity: 0.7 }}>
              The wizard will create one Contract per distinct TIN. Provide the
              legal entity name for each TIN as it should appear on the Merchant
              / ProviderAccount.
            </HHTypography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 160 }}>TIN</TableCell>
                    <TableCell sx={{ width: 100 }}>Locations</TableCell>
                    <TableCell>Legal entity name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgGroups.map(g => (
                    <TableRow key={g.tax_id}>
                      <TableCell
                        sx={{
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                          fontSize: 13,
                        }}
                      >
                        {g.tax_id}
                      </TableCell>
                      <TableCell>{g.locations.length}</TableCell>
                      <TableCell>
                        <HHTextField
                          hhVariant="variant-bypass"
                          size="small"
                          fullWidth
                          placeholder="e.g. Acme Physical Therapy LLC"
                          value={orgNames[g.tax_id] ?? ''}
                          onChange={(e): void =>
                            setOrgName(g.tax_id, e.target.value)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </HHStack>
        </Paper>
      )}

      {state.input != null && (
        <HHStack spacing={2}>
          <Tabs
            value={tab}
            onChange={(_, v): void => setTab(v as 'summary' | 'tree')}
            sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
          >
            <Tab value="summary" label="Summary" />
            <Tab value="tree" label="Tree" />
          </Tabs>
          {tab === 'summary' && <InputSummaryView input={state.input} />}
          {tab === 'tree' && parsedForTree != null && (
            <JsonTree value={parsedForTree} />
          )}
        </HHStack>
      )}

      <StepNav
        id="intake"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
        hideBack
      />
    </HHStack>
  );
};
